"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { BottomNav } from "../../../_components/bottom-nav";
import { ExploreFooter } from "../../../_components/explore-footer";
import { buildExploreToolHref } from "../../../_lib/explore-detail-hrefs";
import { useExploreRoute } from "../../../_lib/explore-route-context";
import type { AllProductsItem } from "../../../types";
import type { ToolCategoryDetailData } from "../data";

export function ToolCategoryDetailPage({
  data,
}: {
  data: ToolCategoryDetailData;
}) {
  const { exploreBase } = useExploreRoute();
  const { category, products } = data;

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) {
      return products;
    }
    const q = query.trim().toLowerCase();
    return products.filter(
      (p) =>
        p.displayName.toLowerCase().includes(q) ||
        (p.tagline ?? "").toLowerCase().includes(q) ||
        (p.companyName ?? "").toLowerCase().includes(q)
    );
  }, [products, query]);

  return (
    <main
      className="pb-[calc(var(--explore-bottom-nav-h)+var(--explore-safe-bottom)+16px)] lg:pb-0"
      id="main-content"
      tabIndex={-1}
    >
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6">
        <div className="py-6 pb-4">
          <nav
            aria-label="Breadcrumb"
            className="mb-3 text-[0.8rem] text-[var(--explore-text-muted)]"
          >
            <Link
              className="hover:text-[var(--explore-text-secondary)]"
              href={`${exploreBase}/tools`}
            >
              All Tools
            </Link>
            <span className="mx-2">›</span>
            <Link
              className="hover:text-[var(--explore-text-secondary)]"
              href={`${exploreBase}/tools/categories`}
            >
              Categories
            </Link>
            <span className="mx-2">›</span>
            <span className="text-[var(--explore-text-secondary)]">
              {category.displayName}
            </span>
          </nav>

          <div className="flex items-center gap-3">
            {category.emoji && (
              <div className="flex h-12 w-12 items-center justify-center rounded-[12px] bg-[var(--explore-bg-highlight)] text-[1.6rem]">
                {category.emoji}
              </div>
            )}
            <div>
              <h1
                className="font-bold text-[var(--explore-text-primary)]"
                style={{
                  fontFamily: "var(--explore-card-title-stack)",
                  fontSize: "32px",
                  lineHeight: "1.2",
                  fontWeight: 700,
                }}
              >
                {category.displayName}
              </h1>
              <div className="mt-1 text-[0.85rem] text-[var(--explore-text-muted)]">
                {products.length} {products.length === 1 ? "tool" : "tools"}
              </div>
            </div>
          </div>

          {category.description && (
            <p className="mt-3 max-w-[720px] text-[1rem] text-[var(--explore-text-secondary)]">
              {category.description}
            </p>
          )}
        </div>

        <div className="pb-6">
          <div className="relative max-w-[360px]">
            <svg
              aria-hidden="true"
              className="-translate-y-1/2 absolute top-1/2 left-3.5 h-4 w-4 text-[var(--explore-text-muted)]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              className="w-full rounded-full border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] py-2 pr-4 pl-10 text-[0.85rem] text-[var(--explore-text-secondary)] outline-none transition-all focus:border-[var(--explore-accent-teal)] focus:ring-[3px] focus:ring-[var(--explore-focus-ring)]"
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search within category..."
              type="text"
              value={query}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 pb-12 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((product) => (
            <ProductCard
              exploreBase={exploreBase}
              key={product.slug}
              product={product}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-12 text-center text-[var(--explore-text-muted)]">
            {products.length === 0
              ? "No tools tagged to this category yet."
              : "No tools match your search."}
          </div>
        )}
      </div>

      <ExploreFooter />
      <BottomNav />
    </main>
  );
}

function ProductCard({
  product,
  exploreBase,
}: {
  product: AllProductsItem;
  exploreBase: string;
}) {
  return (
    <Link
      className="hover:-translate-y-0.5 relative flex flex-col rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] p-4 no-underline shadow-[var(--explore-inline-shadow)] transition-all hover:border-[var(--explore-border)] hover:shadow-[var(--explore-shadow-hover)]"
      href={buildExploreToolHref({
        exploreBase,
        productSlug: product.slug,
      })}
    >
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[var(--explore-bg-highlight)]">
          {product.iconUrl ? (
            <Image
              alt={product.displayName}
              className="h-full w-full rounded-[10px] object-cover"
              height={44}
              src={product.iconUrl}
              width={44}
            />
          ) : (
            <span className="text-[1.4rem]">
              {product.emoji ?? product.displayName.charAt(0)}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-[0.95rem] text-[var(--explore-text-primary)]">
            {product.displayName}
          </div>
          {product.companyName && (
            <div className="text-[0.7rem] text-[var(--explore-text-muted)]">
              {product.companyName}
            </div>
          )}
        </div>
      </div>
      {product.tagline && (
        <div className="line-clamp-2 text-[0.8rem] text-[var(--explore-text-secondary)] leading-[1.45]">
          {product.tagline}
        </div>
      )}
      <div className="mt-auto pt-2 text-[0.75rem] text-[var(--explore-text-muted)]">
        {product.storyCount} {product.storyCount === 1 ? "story" : "stories"}
      </div>
    </Link>
  );
}
